/** SUNTRONIX ONE — canonical Customer Hierarchy selector.
 *  Resolves a PARENT customer's authorised child entities (subsidiaries,
 *  customer entities, legal entities) from the single hierarchy endpoint.
 *  Reusable across CSR, Service Desk, Deployment, Recovery, Shipment, etc.
 */
import { useCallback, useEffect, useRef } from "react";
import api from "@/lib/api";
import { AsyncCombobox } from "@/components/app/AsyncCombobox";

export const PARENT_ENTITY = "__PARENT__";

const TYPE_LABEL = {
  SUBSIDIARY: "Subsidiary", COMPANY: "Subsidiary", BUSINESS_UNIT: "Business unit",
  LEGAL_ENTITY: "Legal entity", CUSTOMER_ENTITY: "Customer entity", PARENT: "Parent customer",
};
const label = (t) => TYPE_LABEL[String(t || "").toUpperCase()] || (t || "Entity");

export function CustomerEntitySelector({
  parentId, parentName, value, valueLabel, onChange,
  allowParentDirect = true, sourceFilter = null, testId = "entity-selector",
}) {
  const autoParent = useRef(null);
  // Restrict which hierarchy sources are selectable (e.g. ["company"] when the
  // caller needs a valid company_id and cannot use legal-entity ids).
  const keep = useCallback((items) => (sourceFilter
    ? items.filter((e) => sourceFilter.includes(e.source))
    : items), [sourceFilter]);

  const fetcher = useCallback(async (q) => {
    if (!parentId) return [];
    const { data } = await api.get(`/customer-hierarchy/${parentId}/entities`, { params: { q, limit: 50 } });
    const opts = keep(data.items || []).map((e) => ({
      value: e.id,
      label: e.name,
      sub: [label(e.entity_type), e.city, e.country].filter(Boolean).join(" · "),
      _item: e,
    }));
    if (allowParentDirect) {
      const term = (q || "").trim().toLowerCase();
      const match = !term || (parentName || "").toLowerCase().includes(term)
        || "parent no subsidiary direct".includes(term);
      if (match) {
        opts.unshift({
          value: PARENT_ENTITY,
          label: `Use ${parentName || "parent customer"} directly`,
          sub: "Parent customer · no subsidiary",
          _item: { entity_type: "PARENT", name: parentName, id: parentId },
        });
      }
    }
    return opts;
  }, [parentId, keep, allowParentDirect, parentName]);

  // Auto-select when the parent has exactly one child entity.
  useEffect(() => {
    if (!parentId || value || autoParent.current === parentId) return;
    autoParent.current = parentId;
    let cancelled = false;
    api.get(`/customer-hierarchy/${parentId}/entities`).then(({ data }) => {
      if (cancelled) return;
      const items = keep(data.items || []);
      if (items.length === 1) {
        const e = items[0];
        onChange(e.id, { value: e.id, label: e.name, _item: e });
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [parentId, value, onChange, keep]);

  return (
    <div data-testid={testId}>
      <AsyncCombobox
        value={value} selectedLabel={valueLabel} onChange={onChange} fetcher={fetcher}
        minChars={0} disabled={!parentId}
        placeholder={parentId
          ? (allowParentDirect ? "Pick subsidiary / entity (or use parent)" : "Pick subsidiary / entity")
          : "Select a customer first"}
        emptyText="No linked subsidiaries / entities found."
        testId={`${testId}-combo`} />
    </div>
  );
}
